import React from "react"
import PropTypes from "prop-types"
import clsx from "clsx"
import Link from "next/link"

// Material UI
import { makeStyles } from "@material-ui/core/styles"
import { Grid, Paper, Avatar, Typography, Hidden } from "@material-ui/core"

// Components
import Button from "components/shared/Button"

// Icons
import TrendingUpIcon from "@material-ui/icons/TrendingUp"
import VisibilityIcon from "@material-ui/icons/Visibility"
import StarRateIcon from "@material-ui/icons/StarRate"
import PeopleIcon from "@material-ui/icons/People"
import ListIcon from "@material-ui/icons/List"
import ScheduleIcon from "@material-ui/icons/Schedule"

// Helpers
import { getPlatformURL, getPluralOrSingular } from "shared/helpers"

const useStyles = makeStyles(theme => ({
  paper: {
    // minHeight: theme.spacing(36),
    display: "flex",
    flexDirection: "column",
    height: "100%"
  },
  avatar: {
    width: "100%",
    minHeight: theme.spacing(16)
  },
  typography: {
    display: "flex",
    alignItems: "center"
  },
  icon: {
    marginRight: theme.spacing(0.5)
  },
  yellowIcon: {
    color: theme.palette.warning.main
  },
  contentPadding: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    paddingTop: theme.spacing(2)
  },
  paddingBottom: {
    paddingBottom: theme.spacing(2)
  },
  centerAlign: {
    textAlign: "center"
  },

  // For large screens
  avatarLarge: {
    width: "100%",
    height: theme.spacing(18)
  },
  contentContainer: {
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(1, 3)
  },
  description: {
    display: "-webkit-box",
    "-webkit-line-clamp": 3,
    "-webkit-box-orient": "vertical",
    overflow: "hidden"
  },
  fullHeight: {
    height: "100%"
  }
}))

export default function CourseItem({
  type,
  uuid,
  slug,
  name,
  image,
  description,
  isActive,
  allowDemo,
  isPurchased,
  // Icons
  number_of_enrollments,
  rating,
  items,
  level,
  prices = [],
  duration
}) {
  const classes = useStyles()

  return (
    <Paper className={classes.paper}>
      <Hidden smUp>
        <Avatar
          alt="item"
          variant="rounded"
          src={image}
          className={classes.avatar}
        >
          <img
            alt="placeholder"
            width="100%"
            height="100%"
            src="/store/assets/placeholders/course_placeholder.svg"
          />
        </Avatar>
        <Typography
          variant="h5"
          className={clsx("text-bold", classes.contentPadding)}
        >
          {name}
        </Typography>
        <Typography
          variant="body1"
          className={clsx(classes.contentPadding, classes.description)}
        >
          {description}
        </Typography>

        <div
          className={clsx(classes.contentPadding, {
            [classes.paddingBottom]: !isActive
          })}
        >
          <Icons
            number_of_enrollments={number_of_enrollments}
            rating={rating}
            items={items}
            level={level}
            learning_mode={prices.map(obj => obj.learning_mode)}
            duration={duration}
          />
        </div>
        <Grid container>
          {/* <Grid item xs={6} className={classes.contentPadding}>
            <Typography>
              {prices[0]?.currency.symbol}
              {prices[0]?.price}
            </Typography>
          </Grid> */}
          <Grid item xs={6}>
            {type === "course" && isPurchased ? (
              <Button
                component="a"
                href={getPlatformURL("learn", `/course/${slug}`)}
                disabled={!isActive}
                variant="text"
                className={clsx(classes.contentPadding, classes.paddingBottom)}
                color="primary"
              >
                View
              </Button>
            ) : (
              <Link as={`/${type}s/${slug}`} href={`/${type}s/[${type}Slug]`}>
                <Button
                  disabled={!isActive && !type === "course"}
                  variant="text"
                  className={clsx(
                    classes.contentPadding,
                    classes.paddingBottom
                  )}
                  color="primary"
                >
                  View
                </Button>
              </Link>
            )}
          </Grid>
        </Grid>
      </Hidden>

      <Hidden xsDown>
        <Grid container>
          <Grid item sm={2}>
            <Avatar
              alt="course"
              variant="rounded"
              src={image}
              className={classes.avatarLarge}
            >
              <img
                alt="placeholder"
                width="100%"
                height="100%"
                src="/store/assets/placeholders/course_placeholder.svg"
              />
            </Avatar>
          </Grid>
          <Grid item sm={8} md={9} className={classes.contentContainer}>
            <div
              style={{
                flexGrow: 1
              }}
            >
              <Typography variant="h6" className="text-bold">
                {name}
              </Typography>
              <Typography variant="body1" className={classes.description}>
                {description}
              </Typography>
            </div>

            <Icons
              number_of_enrollments={number_of_enrollments}
              rating={rating}
              items={items}
              level={level}
              learning_mode={prices.map(obj => obj.learning_mode)}
              duration={duration}
            />
          </Grid>
          <Grid item container sm={2} md={1} alignContent="center">
            <Grid item sx={12}>
              <div>
                {/* <Typography align="center">
                  {prices[0]?.currency.symbol}
                  {prices[0]?.price}
                </Typography> */}
                {isPurchased && type === "course" ? (
                  <Button
                    component="a"
                    href={getPlatformURL("learn", `/course/${slug}`)}
                    disabled={!isActive}
                    className={clsx(
                      classes.contentPadding,
                      classes.paddingBottom,
                      classes.centerAlign
                    )}
                    variant="text"
                    color="primary"
                  >
                    View
                  </Button>
                ) : (
                  <Link
                    as={`/${type}s/${slug}`}
                    href={`/${type}s/[${type}Slug]`}
                  >
                    <Button
                      disabled={!isActive && !type === "course"}
                      variant="text"
                      color="primary"
                      className={clsx(
                        classes.contentPadding,
                        classes.paddingBottom,
                        classes.centerAlign
                      )}
                    >
                      View
                    </Button>
                  </Link>
                )}
              </div>
            </Grid>
          </Grid>
        </Grid>
      </Hidden>
    </Paper>
  )
}

CourseItem.propTypes = {
  slug: PropTypes.string.isRequired,
  uuid: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  image: PropTypes.string.isRequired,
  description: PropTypes.string,
  isActive: PropTypes.bool,
  isPurchased: PropTypes.bool,
  allowDemo: PropTypes.bool,
  type: PropTypes.string.isRequired,
  // Icons
  number_of_enrollments: PropTypes.number,
  rating: PropTypes.number,
  items: PropTypes.number,
  level: PropTypes.string,
  prices: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
  duration: PropTypes.number
}

function Icons({
  number_of_enrollments,
  rating,
  items,
  level,
  learning_mode,
  duration
}) {
  const classes = useStyles()

  return (
    <Grid container direction="column" justifyContent="space-between">
      <Grid container item spacing={1}>
        {number_of_enrollments !== undefined && number_of_enrollments > 300 && (
          <Grid item xs={6} sm="auto">
            <Typography
              className={classes.typography}
              display="inline"
              variant="body2"
              color="textSecondary"
            >
              <PeopleIcon className={clsx(classes.icon, classes.yellowIcon)} />
              {getPluralOrSingular("Learner", number_of_enrollments)}
            </Typography>
          </Grid>
        )}

        {rating !== undefined && (
          <Grid item xs={6} sm="auto">
            <Typography
              className={classes.typography}
              display="inline"
              variant="body2"
              color="textSecondary"
            >
              <StarRateIcon
                className={clsx(classes.icon, classes.yellowIcon)}
              />
              {getPluralOrSingular("Star", 5)}
            </Typography>
          </Grid>
        )}

        {level !== undefined && (
          <Grid item xs={6} sm="auto">
            <Typography
              className={clsx(classes.typography, "text-capitalize")}
              display="inline"
              variant="body2"
              color="textSecondary"
            >
              <TrendingUpIcon className={classes.icon} />
              {level}
            </Typography>
          </Grid>
        )}
        {learning_mode !== undefined && learning_mode.length !== 0 && (
          <Grid item xs={6} sm="auto">
            <Typography
              className={classes.typography}
              display="inline"
              variant="body2"
              color="textSecondary"
            >
              <VisibilityIcon className={classes.icon} />
              {learning_mode &&
                (learning_mode.length > 1 ? (
                  <span>
                    <span className="text-capitalize">
                      {learning_mode[0].mode}
                    </span>
                    &nbsp; + {learning_mode.length - 1} more
                  </span>
                ) : (
                  <span className="text-capitalize">
                    {learning_mode[0].mode}
                  </span>
                ))}
            </Typography>
          </Grid>
        )}

        {items !== undefined && (
          <Grid item xs={6} sm="auto">
            <Typography
              className={classes.typography}
              display="inline"
              variant="body2"
              color="textSecondary"
            >
              <ListIcon className={classes.icon} />
              {getPluralOrSingular("Item", items)}
            </Typography>
          </Grid>
        )}

        {duration !== undefined && (
          <Grid item xs={6} sm="auto">
            <Typography
              className={classes.typography}
              display="inline"
              variant="body2"
              color="textSecondary"
            >
              <ScheduleIcon className={classes.icon} />
              {getPluralOrSingular("Day", duration)}
            </Typography>
          </Grid>
        )}
      </Grid>
    </Grid>
  )
}

Icons.propTypes = {
  number_of_enrollments: PropTypes.number,
  rating: PropTypes.number,
  items: PropTypes.number,
  level: PropTypes.string,
  learning_mode: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
  duration: PropTypes.number
}
